import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { CustomerSelectorComponent } from '../../shared/design-system/customer-selector.component';
import { AinAmountPipe } from '../../shared/design-system/ain-amount.pipe';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { SalesAiCopilotPanelComponent } from './sales-ai-copilot-panel.component';
import { REGULATED_PANEL_STYLES } from './sales-regulated-panel.styles';
import { RegulatedInsuranceProposalRecord, RegulatedInsuranceQuoteRecord, SalesRegulatedApiService } from './sales-regulated-api.service';

type InsTab = 'overview' | 'proposals' | 'actions';

@Component({
  standalone: true,
  selector: 'ain-sales-insurance-quote-detail',
  imports: [
    AinPageComponent, AinStateComponent, RouterLink, FormsModule, ButtonModule,
    InputTextModule, InputNumberModule, TableModule, CustomerSelectorComponent, CurrencySelectorComponent,
    AinAmountPipe, AinDatePipe, SalesAiCopilotPanelComponent
  ],
  template: `
    <ain-page [title]="isCreate() ? t('sales.regulated.insurance.newQuote') : t('sales.regulated.insurance.detailTitle')" [description]="t('sales.regulated.insurance.description')">
      @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> } @else {
        <div class="layout">
          <section class="main">
            <header class="card"><a class="back-link" [routerLink]="['/sales/insurance-quotes']">{{ t('catalog.action.backToList') }}</a></header>
            @if (isCreate()) {
              <section class="card grid">
                <label><span>{{ t('sales.regulated.insurance.fields.product') }}</span><input pInputText [(ngModel)]="form.productCode" /></label>
                <label><span>{{ t('sales.regulated.insurance.fields.premium') }}</span><p-inputNumber [(ngModel)]="form.premiumAmount" [min]="0" /></label>
                <label><span>{{ t('sales.installments.fields.currency') }}</span><ain-currency-selector [(ngModel)]="form.currencyCode" /></label>
                <label class="full"><span>{{ t('sales.orders.fields.customer') }}</span><ain-customer-selector [(ngModel)]="form.customerAccountId" /></label>
                <button pButton type="button" [label]="t('common.save')" (click)="save()"></button>
              </section>
            } @else if (quote(); as q) {
              <section class="summary card">
                <article><span>{{ t('sales.regulated.insurance.fields.product') }}</span><strong>{{ q.productCode }}</strong></article>
                <article><span>{{ t('sales.regulated.insurance.fields.premium') }}</span><strong>{{ q.premiumAmount | ainAmount }} {{ q.currencyCode }}</strong></article>
                <article><span>{{ t('columns.status') }}</span><strong>{{ q.status }}</strong></article>
                <article><span>{{ t('sales.regulated.insurance.fields.validUntil') }}</span><strong>{{ q.validUntilUtc | ainDate:'datetime' }}</strong></article>
              </section>
              <nav class="tabs">
                @for (tab of tabs; track tab) {
                  <button type="button" [class.active]="activeTab() === tab" (click)="activeTab.set(tab)">{{ tabLabel(tab) }}</button>
                }
              </nav>
              @if (activeTab() === 'proposals') {
                <p-table [value]="proposals()">
                  <ng-template pTemplate="header"><tr><th>{{ t('sales.regulated.insurance.fields.proposalNumber') }}</th><th>{{ t('sales.regulated.insurance.fields.premium') }}</th><th>{{ t('columns.status') }}</th></tr></ng-template>
                  <ng-template pTemplate="body" let-row><tr><td>{{ row.proposalNumber }}</td><td>{{ row.proposedPremium | ainAmount }}</td><td>{{ row.status }}</td></tr></ng-template>
                </p-table>
              }
              @if (activeTab() === 'actions') {
                <section class="card grid">
                  <label><span>{{ t('sales.regulated.insurance.fields.proposalNumber') }}</span><input pInputText [(ngModel)]="proposalForm.proposalNumber" /></label>
                  <label><span>{{ t('sales.regulated.insurance.fields.proposedPremium') }}</span><p-inputNumber [(ngModel)]="proposalForm.proposedPremium" [min]="0" /></label>
                  <button pButton type="button" [label]="t('sales.regulated.insurance.createProposal')" (click)="createProposal()"></button>
                  <button pButton type="button" severity="secondary" [label]="t('sales.regulated.insurance.issuePolicy')" (click)="issuePolicy()"></button>
                </section>
              }
            }
          </section>
          @if (!isCreate() && quote()) {
            <ain-sales-ai-copilot-panel entityType="InsuranceQuote" [entityId]="quote()!.id" />
          }
        </div>
      }
    </ain-page>
  `,
  styles: [REGULATED_PANEL_STYLES]
})
export class SalesInsuranceQuoteDetailComponent implements OnInit {
  private readonly api = inject(SalesRegulatedApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  readonly loading = signal(true);
  readonly isCreate = signal(false);
  readonly quote = signal<RegulatedInsuranceQuoteRecord | null>(null);
  readonly proposals = signal<RegulatedInsuranceProposalRecord[]>([]);
  readonly activeTab = signal<InsTab>('overview');
  readonly tabs: InsTab[] = ['overview', 'proposals', 'actions'];
  form = { productCode: 'LIFE-01', premiumAmount: 0, currencyCode: 'IRR', customerAccountId: null as string | null };
  proposalForm = { proposalNumber: '', proposedPremium: 0 };
  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('quoteId') ?? '';
    this.isCreate.set(!id || id === 'new');
    if (this.isCreate()) { this.loading.set(false); return; }
    this.reload(id);
  }
  reload(id: string): void {
    this.api.getInsuranceQuote(id).subscribe({
      next: q => { this.quote.set(q); this.proposalForm.proposedPremium = q.premiumAmount; this.loading.set(false); },
      error: () => this.loading.set(false)
    });
    this.api.listInsuranceProposals(id).subscribe({ next: p => this.proposals.set(p) });
  }
  save(): void {
    this.api.createInsuranceQuote(this.form).subscribe({
      next: q => void this.router.navigate(['/sales/insurance-quotes', q.id])
    });
  }
  createProposal(): void {
    const q = this.quote();
    if (!q) return;
    this.api.createInsuranceProposal({ quoteId: q.id, ...this.proposalForm }).subscribe({
      next: () => this.reload(q.id)
    });
  }
  issuePolicy(): void {
    const q = this.quote();
    if (!q) return;
    const proposalId = this.proposals()[0]?.id ?? null;
    void this.router.navigate(['/sales/insurance-policies/new'], {
      queryParams: {
        quoteId: q.id,
        proposalId,
        customerAccountId: q.customerAccountId,
        premium: q.premiumAmount
      }
    });
  }
  tabLabel(tab: InsTab): string {
    const map: Record<InsTab, string> = {
      overview: 'sales.regulated.tabs.overview',
      proposals: 'sales.regulated.insurance.tabs.proposals',
      actions: 'sales.regulated.tabs.actions'
    };
    return this.t(map[tab]);
  }
  t(key: string): string { return this.localization.translate(key); }
}
